import './conversation-center.scss';
import { FunctionComponent, useContext, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import * as QueryString from 'query-string';
import { Loader } from 'concert-ui-library';
import { BreadCrumb } from 'primereact/breadcrumb';
import { MenuItem, MenuItemCommandEvent } from 'primereact/menuitem';
import { MessageForm, UserType } from './message-sender';
import { ConversationViewer } from './conversation-viewer';
import { NotificationContext } from '../../notification-context';
import {
    Conversation,
    ConversationInput,
    ConversationMessage,
    MessageUserType,
    SendConversationMessageInput,
    useCreateConversationMutation,
    useLazyGetConversationQuery,
    useMarkConversationMessagesOpenedMutation,
    useSendConversationMessageMutation,
} from '../../services/graphql/generated';
import { ROUTE_PARAMS, ROUTES } from '../../constants';

interface ConversationState {
    conversation?: Conversation | null;
}

export const ConversationCenter: FunctionComponent = () => {
    const placeholderErrorMessage = 'Something Went wrong while fetching the messages.';
    const placeHolderReplyMessageError = 'Something went wrong while sending the message';
    const currentLocation = useLocation();
    const { state } = currentLocation as { state: ConversationState };
    const history = useHistory();
    const notificationContext = useContext(NotificationContext);
    const [conversationState, setConversationState] = useState<Conversation | null>(null);
    const [conversationQuery, { data: conversation, error: conversationError, isFetching }] =
        useLazyGetConversationQuery();
    const [
        createConversation,
        {
            isLoading: isCreationInProgress,
            isSuccess: creationSucceeded,
            error: creationError,
            data: createConversationResponse,
        },
    ] = useCreateConversationMutation();
    const [markConversationAsOpened] = useMarkConversationMessagesOpenedMutation();

    const [
        sendMessage,
        {
            data: sendMessageResponse,
            error: sendMessageError,
            isSuccess: messageReplySucceeded,
            isLoading: isMessageSendingInProgress,
        },
    ] = useSendConversationMessageMutation();

    const { conversationId, patientId } = QueryString.parse(currentLocation.search);
    const isNewConversation = !conversationId;
    const isBusy = () => isFetching || isCreationInProgress || isMessageSendingInProgress;

    useEffect(() => {
        const getConversation = async () => {
            const payload = await conversationQuery({
                request: {
                    conversationId: conversationId as string,
                },
            }).unwrap();

            const latestMessageCreatedDateUtc =
                payload?.getConversation?.conversation?.messages?.[0]?.createDate ?? null;
            if (latestMessageCreatedDateUtc) {
                await markConversationAsOpened({
                    input: {
                        conversationId: conversationId as string,
                        lastOpenedMessageCreateDate: latestMessageCreatedDateUtc,
                    },
                });
            }
        };
        if (conversationId && !isFetching && !conversation && !conversationError) {
            getConversation();
        }
    }, []);

    const handleConversationCreation = (formData: MessageForm) => {
        const creationInput: ConversationInput = {
            episodeId: formData.episode?.id as string,
            message: {
                message: formData.message,
                recipientId: (formData.episode?.patientId as string) ?? (formData.episode?.clinicianId as string),
            },
            topic: formData.topic as string,
        };
        createConversation({ input: creationInput });
    };
    const getFirstMessage = (): ConversationMessage | null => {
        const { messages } = conversationState as Conversation;
        const firstMessage = messages ? messages[0] : null;
        return firstMessage;
    };
    const handleMessageSending = (formData: MessageForm) => {
        const { senderId } = getFirstMessage() as ConversationMessage;
        const inputMessage: SendConversationMessageInput = {
            conversationId: conversationId as string,
            message: {
                message: formData.message,
                recipientId: senderId,
            },
        };
        sendMessage({ input: inputMessage });
    };

    const isReplyAllowed = () => getFirstMessage()?.senderType === MessageUserType.Clinician;
    const handleMessageSubmission = (formData: MessageForm) => {
        if (formData.isValid) {
            isNewConversation ? handleConversationCreation(formData) : handleMessageSending(formData);
        } else {
            notificationContext?.showError('Please fill in the required fields for the message');
        }
    };
    const cancelMessageSubmission = () => {
        history.push({
            pathname: ROUTES.MESSAGES,
            search: `?${ROUTE_PARAMS.PATIENT_ID}=${patientId}`,
            state: {
                refresh: true,
            },
        });
    };

    const goHome = (_: MenuItemCommandEvent): void => {
        history.push(`${ROUTES.MESSAGES}?${ROUTE_PARAMS.PATIENT_ID}=${patientId}`, {
            refresh: true,
        });
    };

    const getHomeBreadcrumb = (): MenuItem => {
        return {
            id: 'home',
            label: 'Messages',
            command: goHome,
        };
    };

    const getConversationTopicFromLocationState = (): string | null => {
        if (conversationState) {
            return conversationState?.topic ?? '';
        }

        if (state?.conversation) {
            return String(state.conversation.topic) ?? '';
        }

        return null;
    };

    const getNavigationModel = (): MenuItem[] => {
        const navigationModel = new Array<MenuItem>();
        const conversationTopic = getConversationTopicFromLocationState();

        if (conversationTopic) {
            navigationModel.push({
                id: 'conversation',
                label: conversationTopic,
                className: 'unclickable active',
            });
        }

        return navigationModel;
    };

    useEffect(() => {
        if (conversationError) {
            notificationContext?.showError(placeholderErrorMessage);
            return;
        }
        if (creationError) {
            notificationContext?.showError(placeHolderReplyMessageError);
            return;
        }
        if (sendMessageError) {
            notificationContext?.showError(placeHolderReplyMessageError);
        }
    }, [conversationError, creationError, sendMessageError]);

    useEffect(() => {
        if (messageReplySucceeded && sendMessageResponse) {
            const conversationMessages = conversationState?.messages ? [...conversationState.messages] : [];
            conversationMessages?.unshift(sendMessageResponse.sendConversationMessage!);
            setConversationState({ ...conversationState!, messages: conversationMessages });
            return;
        }
        if (!conversationState && conversation) {
            setConversationState(conversation!.getConversation!.conversation!);
            return;
        }
        if (!conversationState && creationSucceeded && createConversationResponse) {
            const newConversation: Conversation = {
                ...createConversationResponse.createConversation!,
                hasUnopenedMessages: false,
            };
            newConversation && setConversationState(newConversation);
        }
    }, [conversationError, creationError, conversation, creationSucceeded, messageReplySucceeded]);
    return (
        <div>
            <div className="app-container message-center-container">
                <BreadCrumb model={getNavigationModel()} home={getHomeBreadcrumb()} />
                {isBusy() ? (
                    <Loader />
                ) : (
                    <ConversationViewer
                        conversation={conversationState}
                        messageSenderComponentProps={{
                            userType: UserType.patient,
                            isNewConversation,
                            isFormVisible: conversationId === undefined,
                            disabled: conversationState !== null && !isReplyAllowed(),
                            handleMessageSubmission,
                            cancelMessageSubmission,
                        }}
                    />
                )}
            </div>
        </div>
    );
};
